import { useMutation } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';

type PreferredAutoRenewalPatchBody =
  paths['/offers/preferred-term/{uuid}/auto-renewal']['patch']['requestBody']['content']['application/json'];

type PreferredAutoRenewalPatchResponse =
  paths['/offers/preferred-term/{uuid}/auto-renewal']['patch']['responses']['200']['content']['application/json'];

export interface UpdatePreferredAutoRenewalInput {
  takerMarket: TakerMarket;
  renewal: PreferredAutoRenewalPatchBody;
}

const useUpdatePreferredAutoRenewal = () => {
  const { refetchOneTakerMarket } = useRefetchStatsQueries();
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();

  return useMutation({
    mutationKey: ['updatePreferredAutoRenewal'],
    mutationFn: async ({ takerMarket, renewal }: UpdatePreferredAutoRenewalInput) => {
      if (renewal.enabled && !renewal.termDays) {
        throw new Error('Term days is required when auto-renewal is enabled');
      }

      const offerUuid = takerMarket.offerConfig.uuid;

      if (!offerUuid) {
        throw new Error('Offer UUID is required');
      }

      return apiClient
        .patch(`api/supplier-experience/offers/preferred-term/${offerUuid}/auto-renewal`, {
          searchParams: qs.stringify(
            { makerOrganizationUuids: [takerMarket.makerOrganizationUuid] },
            { arrayFormat: 'repeat' }
          ),
          json: {
            ...renewal,
          } satisfies PreferredAutoRenewalPatchBody,
        })
        .json<PreferredAutoRenewalPatchResponse>();
    },
    onSuccess: (_, { takerMarket, renewal }) => {
      if (renewal.enabled) {
        trackEnterpriseEvent('offer::auto-renewal::preferred::enabled', {
          offerUuid: takerMarket.offerConfig.uuid,
          divisionId: takerMarket.offerConfig.divisionId,
          marketUuid: takerMarket.offerConfig.marketUuid,
        });
      } else {
        trackEnterpriseEvent('offer::auto-renewal::preferred::disabled', {
          offerUuid: takerMarket.offerConfig.uuid,
          divisionId: takerMarket.offerConfig.divisionId,
          marketUuid: takerMarket.offerConfig.marketUuid,
        });
      }

      refetchOneTakerMarket({
        marketUuid: takerMarket.marketUuid,
        takerId: takerMarket.takerDivisionId,
      });
    },
    onError: (error, { takerMarket, renewal }) => {
      addError('offer::auto-renewal::preferred::error', {
        error,
        offerUuid: takerMarket.offerConfig.uuid,
        divisionId: takerMarket.offerConfig.divisionId,
        marketUuid: takerMarket.offerConfig.marketUuid,
        enableRenewal: renewal.enabled,
      });
    },
  });
};

export default useUpdatePreferredAutoRenewal;
